import React, { createContext, useContext, useEffect, useState } from 'react';
import { authUtils, authService, User, RegisterData } from './auth';

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = authUtils.getToken();
        const storedUser = authUtils.getUser();

        if (token && storedUser) {
          // Check if token is expired
          if (isTokenExpired(token)) {
            try {
              // Try to refresh the token
              await refreshToken();
              // After refresh, get updated user data with permissions
              await refreshUserData();
            } catch (error) {
              console.error('Token refresh failed:', error);
              // Clear invalid auth data
              authUtils.clearAuth();
            }
          } else {
            // Token is valid, but get fresh user data with permissions
            try {
              await refreshUserData();
            } catch (error) {
              console.error('Failed to get user data:', error);
              // Fallback to stored user if API call fails
              setUser(storedUser);
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        authUtils.clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (user) {
      const refreshInterval = setInterval(async () => {
        const token = authUtils.getToken();
        if (token && isTokenExpired(token)) {
          try {
            await refreshToken();
          } catch (error) {
            console.error('Auto token refresh failed:', error);
            logout();
          }
        }
      }, 5 * 60 * 1000); // Check every 5 minutes

      return () => clearInterval(refreshInterval);
    }
  }, [user]);

  const isTokenExpired = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      // Consider token expired if it expires within 5 minutes
      return payload.exp < (currentTime + 300);
    } catch (error) {
      return true;
    }
  };

  const refreshUserData = async (): Promise<void> => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.data?.user) {
        const updatedUser = response.data.user;
        authUtils.setUser(updatedUser);
        setUser(updatedUser);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<void> => {
    setIsLoading(true);
    try {
      const loggedInUser = await authService.login(email, password, rememberMe);
      setUser(loggedInUser);
      
      // After login, refresh user data to get latest permissions
      try {
        await refreshUserData();
      } catch (error) {
        console.error('Failed to refresh user permissions after login:', error);
        // Continue with basic login even if permission refresh fails
      }
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData): Promise<void> => {
    setIsLoading(true);
    try {
      const registeredUser = await authService.register(userData);
      setUser(registeredUser);
      
      // After registration, refresh user data to get latest permissions
      try {
        await refreshUserData();
      } catch (error) {
        console.error('Failed to refresh user permissions after registration:', error);
        // Continue with basic registration even if permission refresh fails
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      // Call backend logout
      await authService.logout();
    } catch (error) {
      console.error('Backend logout error:', error);
    } finally {
      // Clear local auth data regardless of backend response
      authUtils.clearAuth();
      setUser(null);
      setIsLoading(false);
      
      // Redirect to home page
      window.location.href = '/';
    }
  };

  const refreshToken = async (): Promise<void> => {
    try {
      const newToken = await authService.refreshToken();
      authUtils.setToken(newToken);
      
      // Get updated user data if needed
      const currentUser = authUtils.getUser();
      if (currentUser) {
        setUser(currentUser);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.isSuperAdmin) return true; // SuperAdmin has all permissions
    return user.permissions?.includes(permission) || false;
  };

  const isAdmin = (): boolean => {
    return user?.isAdmin || false;
  };

  const isSuperAdmin = (): boolean => {
    return user?.isSuperAdmin || false;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshToken,
    refreshUserData,
    hasPermission,
    isAdmin,
    isSuperAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false 
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = '/login';
    return null;
  }

  if (requireAdmin && (!user?.role || (user.role.name !== 'Admin' && user.role.name !== 'Super Admin'))) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        color: '#ef4444'
      }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  return <>{children}</>;
};
